'use client';

import type { ChainId } from '@morpho-org/blue-sdk';
import type { ReactTable } from '@tanstack/react-table';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { InterfaceChainUtils, toBigInt } from '@morpho-org/dapp-core';
import { TableNoFilterResults } from '@morpho-org/ui-v6/client';
import type { DashboardLendPositionsFilters } from '@/helpers/filter/table';
import type { UserLendPositionFacets } from '@/hooks/positions/userLendPositionsQuery';
import { MobileLendPositionCard } from '@/components/common/MobileBorrowCard';
import { getParsedValue } from '@/helpers/formatters';
import { getMarketUrl, MarketPageTabValues } from '@/helpers/routing';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import { trackPortfolioLendPositionClicked } from '@/lib/analytics/client/trackers/portfolio';
import type { LendPositionsTableFeatures } from '../config/LendPositionsTable.columns';
import type { LendPositionsTableRow } from '../config/LendPositionsTable.types';
import DashboardLendPositionsFiltersComponent from './DashboardLendPositionsFilters';
interface DashboardLendPositionsMobileViewProps {
  table: ReactTable<LendPositionsTableFeatures, LendPositionsTableRow>;
  filters?: DashboardLendPositionsFilters;
  onFiltersChange?: (filters: DashboardLendPositionsFilters) => void;
  positionChainIds?: ChainId[];
  loading: boolean;
  disabled?: boolean;
  totalPositionsCount: number;
  facets?: UserLendPositionFacets;
}
const DashboardLendPositionsMobileView = ({
  table,
  filters,
  onFiltersChange,
  positionChainIds,
  loading,
  disabled,
  totalPositionsCount,
  facets
}: DashboardLendPositionsMobileViewProps) => {
  // 'use no memo': TanStack keeps this object's reference while table state (sorting, rows) changes.
  'use no memo';

  const {
    isSelf
  } = useViewedAccountAddress();
  const hasFilteredResults = table.getRowModel().rows.length > 0;
  return <div className="gap-sm flex flex-col">
      <DashboardLendPositionsFiltersComponent positionChainIds={positionChainIds} filters={filters} onFiltersChange={onFiltersChange} disabled={disabled || loading} facets={facets} />
      {!hasFilteredResults ? <TableNoFilterResults withBorder type="markets" count={totalPositionsCount} clearFilters={() => onFiltersChange?.({
      collateralAssetIdsFilter: [],
      loanAssetIdsFilter: [],
      chainIdsFilter: []
    })} /> : <>
          {table.getRowModel().rows.map(row => {
        const {
          collateral,
          lent
        } = row.original;
        const {
          chainId,
          marketId
        } = row.original.market;

        // This view renders below 1024px but the market page only picks
        // its mobile layout below 768px — send both position anchors so
        // the 768-1024px band (desktop market layout) deep-links too.
        const marketUrl = getMarketUrl({
          identifier: InterfaceChainUtils.getIdentifierById(chainId),
          marketId,
          collateralSymbol: row.original.market.collateralAsset?.symbol ?? '',
          loanSymbol: row.original.market.loanAsset.symbol,
          hash: isSelf ? 'yourPosition' : undefined,
          mobileTab: isSelf ? MarketPageTabValues.MyPosition : undefined
        });
        return <MobileLendPositionCard key={row.id} href={marketUrl} onLinkClick={() => trackPortfolioLendPositionClicked({
          chainId,
          marketId
        })} marketId={marketId} isSelf={isSelf} hasBorrowPosition={row.original.hasBorrowPosition} collateralToken={collateral.token ?? null} lent={{
          token: lent.token,
          amount: toBigInt(lent.amount) ?? null,
          usdValue: getParsedValue(lent.value, safeParseNumber)
        }} lltv={row.original.market.lltv} netApy={row.original.netApy.avgNetSupplyApy} apy={row.original.netApy.avgSupplyApy} rewards={row.original.netApy.rewards} warnings={collateral.warnings} displayNetwork />;
      })}
        </>}
    </div>;
};
export default DashboardLendPositionsMobileView;